/**
 * Créditos, the organisation's credits and slots together (platform design §5.1.1, Figma
 * `185:5432`), and where a newly created organisation lands. One line of three cards -- `Créditos`
 * (this file's `CreditsCard`), `Vagas de campanha` (`CapacityStats`) and `Contas monitoradas`
 * (`AccountsCard`) -- stacking below `lg`, then the consumption chart beside `OpenRequests`.
 *
 * The frame's "Consumo por campanha" list needs a tenant-wide seven-day consumption aggregate no
 * route serves yet, so it does not render rather than shipping an em dash in its place.
 *
 * `Pedir créditos` and `Pedir vagas` are their cards' own triggers and the page's only ones: each
 * opens its dialog over this page and is gone while a request of its kind is open, since the API
 * refuses a second one. The open dialog is the `?pedir=creditos|vagas` search param, so another
 * screen links here with the dialog already open and closing it clears the param.
 *
 * `ShedAccounts` renders below the card row: the accounts §18.5 dropped to buy a usable window.
 */

import type { ReactNode } from 'react';
import { Users } from 'lucide-react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';

import { Absent } from '../../../chrome/measure';
import { Page, PageHeader } from '../../../chrome/page';
import { QueryStates } from '../../../chrome/state';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { getLocale } from '../../../i18n';
import { credits as formatCredits } from '../../../i18n/format';
import { accountsPath } from '../../control/paths';
import { acquisitionState, trackedAccounts, useCredits, useFundedWindow } from '../balance';
import { formatDate } from '../balance/FundedWindowCard';
import { CreditsStatusIcon, MonthCreditsMeter } from '../balance/MonthCreditsMeter';
import { ShedAccounts } from '../balance/ShedAccounts';
import { useCapacity, useConsumption, useHasOpenRequest } from './api/queries';
import { RequestCreditsDialog } from './RequestCredits';
import { useCan } from '../../session/orgContext';
import { tt } from '../i18n';
import { cm } from './messages';
import { CapacityStats } from './CapacityStats';
import { ConsumptionChart } from './Consumption';
import { OpenRequests } from './OpenRequests';
import { RequestSlots } from './RequestSlots';

/** The two-card row's own left card (Figma `363:14814`). `fundedWindow` may be absent (its route
 *  can 404), same tolerance `CreditsMeter` already gives it -- the coverage caption then stays off
 *  rather than waiting on a query this card does not own the retry of. */
function CreditsCard(props: { tenantId: string; trigger?: ReactNode }): ReactNode {
  const { tenantId, trigger } = props;
  const credits = useCredits(tenantId);
  const fundedWindow = useFundedWindow(tenantId);
  const locale = getLocale();

  if (credits.data === undefined) return null;

  const { available } = credits.data;
  const blocked = acquisitionState(credits.data, fundedWindow.data) === 'waiting-on-credits';
  const funded = fundedWindow.data;
  const caption =
    funded === undefined || funded.funded_until === null
      ? undefined
      : cm(blocked ? 'card.credits.stopped' : 'card.credits.coverage', {
          date: formatDate(funded.funded_until, locale, { dateStyle: 'short', timeStyle: 'short' }),
        });

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-body-sm font-medium">{cm('card.credits.heading')}</h2>
          <div className="flex items-center gap-2">
            {trigger}
            <CreditsStatusIcon credits={credits.data} />
          </div>
        </div>
        <span data-numeric className="text-title-lg">
          {blocked ? cm('card.credits.empty') : formatCredits(available, locale)}
        </span>
        <MonthCreditsMeter credits={credits.data} />
        {caption !== undefined && <p className="text-caption text-muted-foreground">{caption}</p>}
      </CardContent>
    </Card>
  );
}

/** Same card as `CreditsCard`, with no action: every tracked account, collected or not. Absent,
 *  not zero, when the funded-window route has no answer. */
function AccountsCard(props: { tenantId: string }): ReactNode {
  const fundedWindow = useFundedWindow(props.tenantId);
  if (fundedWindow.isPending) return null;
  const accounts =
    fundedWindow.data === undefined ? undefined : trackedAccounts(fundedWindow.data).length;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Users aria-hidden className="size-4 text-muted-foreground" />
          <h2 className="text-body-sm font-medium">{cm('card.accounts.heading')}</h2>
        </div>
        <span data-numeric data-testid="capacity-accounts" className="text-title-lg">
          {accounts === undefined ? <Absent reason="not_measured" /> : accounts}
        </span>
      </CardContent>
    </Card>
  );
}

export function Capacity(): ReactNode {
  const params = useParams({ strict: false }) as { tenantId?: string };
  const { pedir } = useSearch({ strict: false }) as { pedir?: unknown };
  const navigate = useNavigate();
  const setPedir = (kind: 'creditos' | 'vagas', open: boolean): void => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, pedir: open ? kind : undefined }),
      replace: !open,
    });
  };
  const tenantId = params.tenantId;
  // Called unconditionally, before the early return below, so hook order
  // never depends on whether the route has resolved a `tenantId` yet.
  const capacityQuery = useCapacity(tenantId ?? '');
  const consumptionQuery = useConsumption(tenantId ?? '');
  // A courtesy: the hidden trigger is not the refusal, the API's own 403 on
  // the POST is (platform design §17.2).
  const canWrite = useCan('control_plane.write');
  const creditRequestOpen = useHasOpenRequest(tenantId ?? '', 'CREDITS');
  const slotRequestOpen = useHasOpenRequest(tenantId ?? '', 'SLOTS');

  if (tenantId === undefined) return null;

  return (
    <Page>
      <PageHeader
        title={cm('page.title')}
        badge={canWrite ? <Badge variant="secondary">{cm('badge.orgAdmin')}</Badge> : undefined}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CreditsCard
          tenantId={tenantId}
          trigger={
            canWrite && creditRequestOpen === false ? (
              <RequestCreditsDialog
                tenantId={tenantId}
                open={pedir === 'creditos'}
                onOpenChange={(open) => setPedir('creditos', open)}
                trigger={
                  <Button variant="outline" size="sm">
                    {tt('creditRequests.create.heading')}
                  </Button>
                }
              />
            ) : undefined
          }
        />
        <QueryStates query={capacityQuery} empty={null}>
          {(capacity) => (
            <CapacityStats
              capacity={capacity}
              trigger={
                canWrite && slotRequestOpen === false ? (
                  <RequestSlots
                    tenantId={tenantId}
                    capacity={capacity}
                    open={pedir === 'vagas'}
                    onOpenChange={(open) => setPedir('vagas', open)}
                    trigger={
                      <Button variant="outline" size="sm">
                        {cm('slots.request')}
                      </Button>
                    }
                  />
                ) : undefined
              }
            />
          )}
        </QueryStates>
        <AccountsCard tenantId={tenantId} />
      </div>

      <ShedAccounts tenantId={tenantId} accountsPathFor={accountsPath} />

      {/* `OpenRequests` reads its own query and owns its own loading/error/empty states, so it
          sits outside `consumptionQuery`'s `QueryStates` -- the chart's own fetch never gates it. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-chart-aside">
        <QueryStates query={consumptionQuery} empty={null}>
          {(consumption) => <ConsumptionChart daily={consumption.daily} />}
        </QueryStates>
        <OpenRequests tenantId={tenantId} />
      </div>
    </Page>
  );
}
